import { useEffect, useId, useMemo, useRef, useState } from 'react';

type Props = {
  id: string;
  value: string;
  disabled?: boolean;
  suggestions: readonly string[];
  invalid?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

/**
 * Combobox наименования клиента: ручной ввод + выбор из уже встречавшихся.
 * Замена браузерного `<datalist>`.
 */
export function ClientNameCombobox({
  id,
  value,
  disabled = false,
  suggestions,
  invalid = false,
  onChange,
  onBlur,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    const items = suggestions.filter((name) => name.trim() !== '');
    if (q === '') {
      return items.slice(0, 20);
    }
    return items.filter((name) => name.toLowerCase().includes(q)).slice(0, 20);
  }, [suggestions, value]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const showList = open && !disabled && filtered.length > 0;

  return (
    <div className="client-combobox" ref={rootRef}>
      <input
        id={id}
        className="field__control"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        autoComplete="off"
        disabled={disabled}
        value={value}
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          onBlur?.();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {showList ? (
        <ul id={listId} className="picker-list client-combobox__list" role="listbox" aria-label="Известные клиенты">
          {filtered.map((name) => (
            <li key={name} role="presentation">
              <button
                type="button"
                className="picker-list__item"
                role="option"
                onMouseDown={(event) => {
                  // mousedown до blur input — иначе список закроется до выбора
                  event.preventDefault();
                  onChange(name);
                  setOpen(false);
                }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
